import { CoreEvent, eventNames } from '@carvana/core-events';
import {
  useEffectWithController,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useEffect } from 'react';
import {
  merchViewedSavedSearches,
  type ViewedSavedSearchItem
} from '~/constants/home';
import UserContext, {
  useIsLoggedIn,
  useUserId
} from '~/context/common/UserContext';
import GlobalContext from '~/context/home/GlobalContext';
import SavedSearchesContext from '~/context/home/SavedSearchesContext';
import { SavedSearch } from '~/contracts/common';
import { readSavedSearch } from '~/services/home/SavedSearchService';
import { BrowserCookies, safeParse } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

function resetNotificationForViewedSearches(searches: SavedSearch[]) {
  const storageItems = sessionStorage.getItem(merchViewedSavedSearches) || '';
  const parsedSearches = safeParse<ViewedSavedSearchItem[]>(storageItems) || [];

  if (!parsedSearches.length) return searches;

  const viewedSearches = parsedSearches.filter(
    storageItem => storageItem.expiration > new Date().getTime()
  );

  return searches.map(search => ({
    ...search,
    totalNewVehicles: viewedSearches.find(
      storageItem => storageItem.cvnaid === search.cvnaId
    )
      ? 0
      : search.totalNewVehicles
  }));
}

export default function useGetSavedSearches() {
  const {
    fetchSavedSearchTrigger,
    savedSearchesState: { setLoading, setData, setLoaded, setError }
  } = useContext(SavedSearchesContext);
  const { triggerRefreshSrpCard } = useContext(GlobalContext);
  const mounted = usePromise();
  const {
    userState: { isLoaded },
    location: {
      data: { zip }
    }
  } = useContext(UserContext);
  const isLoggedIn = useIsLoggedIn();
  const userId = useUserId();

  useEffect(() => {
    function updateSavedSearchData(payload) {
      if (payload.source === 'app' || !payload.searches) return;

      setData(payload.searches);
      triggerRefreshSrpCard();
    }

    CoreEvent.on(eventNames.favorites.mfe.refresh, updateSavedSearchData);

    return () => {
      CoreEvent.off(eventNames.favorites.mfe.refresh, updateSavedSearchData);
    };
  }, [setData, triggerRefreshSrpCard]);

  useEffectWithController(
    controller => {
      if (!isLoaded || !isLoggedIn) {
        setLoaded();

        return;
      }

      setLoading();

      const query = new URLSearchParams({
        browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
        userId,
        zip5: zip
      }).toString();

      mounted(readSavedSearch(query, controller))
        .then(({ data }) => {
          const searches = resetNotificationForViewedSearches(data.reverse());
          setData(searches);
        })
        .catch(
          generateErrorLogger(
            'Get saved searches',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      fetchSavedSearchTrigger,
      isLoaded,
      isLoggedIn,
      mounted,
      setData,
      setError,
      setLoaded,
      setLoading,
      userId,
      zip
    ]
  );
}
